// Copyright © 2021 Move Closer

import { Component } from 'vue-property-decorator'

import { AbstractNavProps, BootstrapLink, BootstrapNav } from '../../dsl/front'
import { isRelated, Related, RelatedType } from '../../services'

import { AbstractModuleUi, CountsArticles, FetchesRelatedSync } from '../_abstract/ui'
import {
  AnyModule,
  AnyObject,
  AppTheme,
  ContentSneakPeakData, ContentType,
  Image,
  ImageFile,
  LineModuleHeader
} from '../_contracts'
import { LineHeader, TileSize } from '../_components/ui'

import {
  getArticlesCount,
  getArticlesCountForTab,
  getArticlesCountPerTab,
  isTeaserBoxWithoutTabs
} from './TeaserBox.helpers'
import { teaserBoxInnerGrids } from './_inner-grids'
import {
  ResolvedTeaserBoxTab,
  TeaserBoxAddonContent,
  TeaserBoxConfig,
  TeaserBoxInnerGrid,
  TeaserBoxModule,
  TeaserBoxTab,
  TeaserBoxVersion
} from './TeaserBox.contracts'
import { prepareImage } from '../_filters'
import { LineHeaderContent } from './_partials'
import { Countdown } from '../_components/Countdown'
import { AddonWithPosition, MergedItem } from './TeaserBox.shared'
import { addonsRegistry } from './TeaserBox.config'

/**
 * Component capable of rendering the `TeaserBoxModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Michał Rossian <michal.rossian@movecloser.pl>
 */
@Component<TeaserBoxModuleUi>({
  name: 'TeaserBoxModuleUi',
  components: {
    BootstrapNav,
    BootstrapLink,
    Countdown,
    LineHeader,
    LineHeaderContent
  },

  created () {
    // if (this.module.content.header.text) {
    //   console.log(`${this.module.content.header.text ?? ''} created:`)
    // }
    if (this.module.content.tabs.length > 0) {
      this.tabs = this.module.content.tabs.map((item, index) => {
        const prefix = this.$id('tab')
        return {
          id: `${prefix}-${index}`,
          label: item.label,
          items: []
        }
      })
    }
    this.fetchRelatedSync()

    if (this.tabs.length > 0) {
      if (this.isWithoutTabs) {
        this.setItemsWithoutTabs()
      }
      this.activeTab = this.tabs[0].id
      // console.log('Items', this.tabs)
      this.onReady()
    } else {
      this.onError()
    }
  },

  template: `
    <section
      :class="['teaser-box', moduleColoredClass, debugClass, loadingClass, versionClass, getThemeByBgClass, brandClass]"
      :style="{ backgroundColor: customColor }"
    >
    <!-- Heading (Premium Zone only) -->
    <h2 v-if="theme === AppTheme.PremiumZone && hasHeaderText" class="teaser-box__title">
      <LineHeaderContent v-if="theme !== AppTheme.PremiumZone"
                         :text="module.content.header.text" :headerImage="headerImage" />
    </h2>

    <!-- Header -->
    <LineHeader v-if="shouldShowHeader || hasMultipleTabs" :showPointer="shouldShowPointer"
                :site="module.content.header ? module.content.header.site : null" :theme="getThemeByBg"
                :isBold="module.content.header.isLink || module.content.header.bold || false"
                :tag="lineHeaderTitleTag"
                class="teaser-box__header">
      <!-- Text -->

      <LineHeaderContent v-if="theme !== AppTheme.PremiumZone && hasHeaderText"
                         :text="module.content.header.text" :headerImage="headerImage" :headerLink="headerLink" />

      <Countdown v-if="module.content.countdownTarget" class="teaser-box__countdown"
                 :target="module.content.countdownTarget" prefix="Już za" />

      <!-- Nav -->
      <template #nav>
        <BootstrapNav v-if="hasMultipleTabs" :activeItem.sync="activeTab" :items="navItems" />
      </template>
    </LineHeader>
    <!-- Contents (inner grid) -->
    <template v-if="!isWithoutTabs">
      <component v-for="tab in tabs"
                 v-if="component"
                 v-show="isTabActive(tab.id)"
                 :is="component"
                 :key="tab.id"
                 :id="tab.id"
                 :label="tab.label"
                 :items="tab.items"
                 :readMoreLink="tab.readMoreLink"
                 v-bind="{ fallbackImage, teaserBoxConfig, relatedService, windowService, shareService, eventBus, isLazy: module.isLazyLoaded, isMobile }"
      />
    </template>
    <template v-else-if="isWithoutTabs && itemsWithoutTabs">
      <component v-if="itemsWithoutTabs && itemsWithoutTabs.items && itemsWithoutTabs.items.length"
                 :is="component"
                 :id="itemsWithoutTabs.id"
                 :label="itemsWithoutTabs.label"
                 :items="itemsWithoutTabs.items"
                 :readMoreLink="itemsWithoutTabs.readMoreLink"
                 v-bind="{ fallbackImage, teaserBoxConfig, relatedService, windowService, shareService, eventBus, isLazy: module.isLazyLoaded, isMobile }" />
    </template>
    </section>
  `
})
export class TeaserBoxModuleUi
  extends AbstractModuleUi<TeaserBoxModule>
  implements CountsArticles, FetchesRelatedSync {
  //
  public readonly AppTheme = AppTheme

  /**
   * Currently-active tab.
   */
  public activeTab: AbstractNavProps['activeItem'] = ''

  public headerImage: Image | null = null
  public headerLink: string | null = null
  /**
   * Tabs' content (resolved using `RelatedService`).
   */
  public tabs: ResolvedTeaserBoxTab[] = []
  public itemsWithoutTabs: ResolvedTeaserBoxTab = {
    id: 'itemsWithoutTabs',
    label: '',
    items: []
  }

  /**
   * @inheritDoc
   */
  public get articlesCount (): number {
    return getArticlesCount(this.moduleVersion, this.tabs.length, this.teaserBoxConfig)
  }

  /**
   * Component capable of rendering the contents from one, specific `TeaserBoxVersion`.
   */
  public get component (): typeof TeaserBoxInnerGrid {
    const component = teaserBoxInnerGrids.find(grid => grid.moduleVersion === this.moduleVersion)

    if (typeof component !== 'function') {
      throw new Error(`TeaserBoxModuleUi.[get]component: Failed to resolve the applicable [TeaserBoxInnerGrid] component for the given version! Current [module.version]: [${this.module.version}].`)
    }
    return component
  }

  public get lineHeaderTitleTag (): string | null {
    if (this.module.content.header) {
      return this.module.content.header.isH1 ? 'h1' : 'h2'
    }

    return 'h2'
  }

  /**
   * @inheritDoc
   */
  public fetchRelatedSync () {
    try {
      const itemsToResolve = Array.isArray(this.module.content.tabs) ? this.module.content.tabs : []
      // console.log('itemsToResolve', itemsToResolve)
      itemsToResolve.map((rawTab, index) => {
        this.resolveSingleTab(rawTab, index)
      })
      if (this.module.content.headerLink) {
        const link = this.relatedService.resolveSync(this.module.content.headerLink) as AnyObject | null

        if (link) {
          this.headerLink = link.urlPath
        }
      }

      if (this.module.content.headerImage) {
        const imageFile: ImageFile | null = this.relatedService.resolveSync(this.module.content.headerImage) as ImageFile | null
        if (imageFile) {
          this.headerImage = prepareImage(imageFile, this.imageRatio)
        }
      }
    } catch (e) {
      this.onError(e)
    }
  }

  /**
   * Determines whether there's a header text to render.
   */
  public get hasHeaderText (): boolean {
    return typeof this.module.content.header !== 'undefined' &&
      typeof this.module.content.header.text === 'string' &&
      this.module.content.header.text.length > 0
  }

  /**
   * Determines whether the module has multiple tabs to render.
   */
  public get hasMultipleTabs (): boolean {
    if (isTeaserBoxWithoutTabs(this.moduleVersion)) {
      return false
    }

    if (typeof this.tabs === 'undefined' || !Array.isArray(this.tabs)) {
      return false
    }
    // console.log('hasMultipleTabs', this.tabs.length)

    return this.tabs.length > 1
  }

  public get isWithoutTabs (): boolean {
    return isTeaserBoxWithoutTabs(this.moduleVersion)
  }

  public getRealAddonPosition (addon: AddonWithPosition): number {
    return (addon.position as number - 1)
  }

  /**
   * Filters and processes positioned addons from raw addon data
   */
  protected getPositionedAddons (addons: AddonWithPosition[]): Array<AddonWithPosition & { position: number }> {
    return addons
      .filter(addon => addon.module && addon.position !== undefined && addon.position !== null && addon.position !== '')
      .map(addon => ({
        ...addon,
        position: this.getRealAddonPosition(addon)
      }))
      .filter(addon => !isNaN(addon.position))
      .sort((a, b) => a.position - b.position)
  }

  /**
   * Gets component type for merged items - shared logic across all components
   */
  protected getComponentType (type: string, driver: string | null = null): string {
    switch (type) {
      case TileSize.L:
      case TileSize.S:
      case TileSize.XL:
        return 'Tile'
      // case 'thumbnails-list-with-addons':
      //   return 'ThumbnailsListWithAddons'
      // case 'text-list-with-addons':
      //   return 'TextListWithAddons'
      case 'addon':
        if (driver && (addonsRegistry as Record<string, string>)[driver]) {
          return (addonsRegistry as Record<string, string>)[driver]
        }
        return 'div'
      default:
        return 'div'
    }
  }

  /**
   * Creates a basic merged list of articles and addons with proper positioning
   * This is used as base for more specific implementations
   */
  protected createMergedItems (
    articles: ContentSneakPeakData[],
    addons: AddonWithPosition[]
  ): MergedItem[] {
    if (!articles || !articles.length) {
      return []
    }
    const mergedList: MergedItem[] = []

    const positionedAddons = this.getPositionedAddons(addons)
    // const unPositionedAddons = this.getUnpositionedAddons(addons)

    let articleIndex = 0
    let currentPosition = 0
    // console.log('articles.length', articles.length)
    const max = (articles.length + positionedAddons.length)
    // Add positioned items (articles and addons)
    while (currentPosition <= max) {
      const addonAtPosition = positionedAddons.find(addon => addon.position === currentPosition)
      if (addonAtPosition) {
        // Add addon at this position
        mergedList.push({
          type: 'addon',
          tileType: 'addon',
          component: this.getComponentType('addon', addonAtPosition.module.driver),
          data: addonAtPosition.module,
          props: {},
          position: currentPosition,
          key: `addon-${Array.isArray(addonAtPosition.module) ? addonAtPosition.module[0]?.driver
            : addonAtPosition.module?.driver}-${currentPosition}`
        })
      } else {
        // Add article at this position
        if (articles[articleIndex]) {
          const tileType = TileSize.S
          const article = articles[articleIndex]
          if (article.type === ContentType.Article) {
            mergedList.push({
              type: article.type,
              tileType: tileType,
              component: this.getComponentType(tileType),
              data: article,
              props: {},
              position: currentPosition,
              key: `${tileType}-${articles[articleIndex].id || articleIndex}`
            })
          }
          articleIndex++
        }
      }

      currentPosition++
    }
    return mergedList.sort((a, b) => a.position - b.position)
  }

  public setItemsWithoutTabs () {
    // if (this.tabs.length !== 2) {
    //   return null
    // }

    const [firstTab, secondTab] = this.tabs
    let items = firstTab.items.concat(secondTab.items)
    items = items.map((item, index) => {
      return { ...item, position: index }
    })
    this.itemsWithoutTabs.id = firstTab.id
    this.itemsWithoutTabs.label = firstTab.label
    this.itemsWithoutTabs.items = items
    if (secondTab.readMoreLink) {
      this.itemsWithoutTabs.readMoreLink = secondTab.readMoreLink
    }
    // console.log('tabForWithoutTabs len', this.itemsWithoutTabs.items.length)
  }

  /**
   * Determines whether tha tab of a passed-in label is the currently-active one.
   *
   * @param label - The label of the tab to check.
   */
  public isTabActive (label: string): boolean {
    return this.activeTab === label
  }

  /**
   * Array of items to be rendered by the `<BootstrapNav>` component.
   */
  public get navItems (): AbstractNavProps['items'] {
    return this.tabs.map(({ id, label }) => ({
      disabled: false,
      id,
      label
    }))
  }

  /**
   * Determines whether there's a header to be rendered.
   */
  public get shouldShowHeader (): boolean {
    const { header = {} } = this.module.content

    if (!this.module.content.showHeader) {
      return false
    }

    const keys: Array<keyof LineModuleHeader> = ['site', 'text']
    for (const key of keys) {
      const value = header[key]
      if (typeof value === 'string' && value.length > 0) {
        return true
      }
    }

    return false
  }

  /**
   * Determines whether the `<LineHeader>` pointer should be shown.
   */
  public get shouldShowPointer (): boolean {
    if (this.theme === AppTheme.PremiumZone) {
      return false
    }

    return !!(this.module.content.header && !this.module.content.header.text && !this.module.content.headerImage)
  }

  /**
   * The CSS class for brand styling based on gradient color domain.
   */
  public get brandClass (): string {
    const { gradientColor } = this.module.content
    if (!gradientColor) {
      return ''
    }

    // Convert domain to CSS class format
    const domain = gradientColor.toLowerCase().replace(/\./g, '-')
    return `--brand-${domain}`
  }

  /**
   * Configuration settings for the `<TeaserBox>` component.
   */
  public get teaserBoxConfig (): TeaserBoxConfig {
    const {
      additionalRows,
      titleVersion,
      showAuthorAvatar,
      showAuthorName,
      showCategory,
      showArticleTags,
      showPublicationDate,
      gradientColor
    } = this.module.content
    return {
      additionalRows,
      showAuthorAvatar,
      showAuthorName,
      showCategory,
      showArticleTags,
      titleVersion,
      showPublicationDate,
      gradientColor
    } as TeaserBoxConfig // FIXME
  }

  /**
   * Resolves length of articles' list displayed by the module.
   */
  // public static resolveArticlesCount (module: TeaserBoxModule, list: TeaserBoxTab['list']): number {
  //   const countPerTab: number = getArticlesCountPerTab(module.version, module.content)
  //   const listId = list.value.toString()
  //   return module.content.tabs.reduce<number>((count: number, tab: TeaserBoxTab) => {
  //     if (!!tab.list && tab.list.value.toString() === listId) {
  //       count += countPerTab
  //     }
  //
  //     return count
  //   }, 0)
  // }

  private get moduleVersion (): TeaserBoxVersion {
    switch (this.module.version) {
      case TeaserBoxVersion['L-2S-big-thumbnails-list']:
      case TeaserBoxVersion['L-2S-text-list']:
      case TeaserBoxVersion['L-2S-thumbnails-list']:
        return TeaserBoxVersion['L-4S-text-list-tabs']
      case TeaserBoxVersion['L-5S-text-list']:
        return TeaserBoxVersion['L-4S-text-list']
      case TeaserBoxVersion['6S-big-thumbnails-list']:
      case TeaserBoxVersion['6S-thumbnails-list']:
      case TeaserBoxVersion['6T-text-list']:
        return TeaserBoxVersion['6S-text-list']
      case TeaserBoxVersion['L-4S']:
      case TeaserBoxVersion['L-8S']:
      case TeaserBoxVersion['8T']:
        return TeaserBoxVersion['8S']
      default:
        return this.module.version
    }
  }

  /**
   * Resolves the "read more" link for the given tab.
   *
   * @param readMoreLink - Related link to resolve.
   * @param index
   */
  private resolveReadMoreLink (
    readMoreLink: Related,
    index: number
  ) {
    let readMoreLinkRelatedTarget
    try {
      readMoreLinkRelatedTarget = this.relatedService.resolveSync(readMoreLink, {})
    } catch (e) {
      if (this.debug) console.warn(e)
    }

    if (typeof readMoreLinkRelatedTarget !== 'undefined' && readMoreLinkRelatedTarget && !Array.isArray(
      readMoreLinkRelatedTarget)) {
      this.tabs[index] = {
        ...this.tabs[index],
        readMoreLink: {
          target: { path: (readMoreLinkRelatedTarget as { urlPath?: string }).urlPath || '' }
        }
      }
    }
  }

  /**
   * Resolves all related data for the passed-in tab.
   *
   * @param rawTab - The tab which content is to be resolved.
   * @param index - Tab's index in `this.tabs` array.
   */
  private resolveSingleTab (rawTab: TeaserBoxTab, index: number) {
    const {
      addons: rawAddons,
      list,
      readMoreLink,
      allowDuplicates,
      startFromBeginning
    } = rawTab

    // console.log(`list ${index}`, list)
    if (typeof list !== 'object' || list === null || !Object.keys(list).length) {
      return
    }
    const articlesCount = this.getArticlesCount(index)
    // console.log(`articlesCount ${index}`, articlesCount)
    let articles: ContentSneakPeakData[] | undefined
    try {
      articles = this.relatedService
        .resolveSync(list, {
          continueOrder: !startFromBeginning,
          preventDuplicates: !allowDuplicates,
          take: articlesCount
        }) as ContentSneakPeakData[]
    } catch (e) {
      if (this.debug) console.warn(e)
    }
    // console.log('articles LEN', articles?.length)
    if (typeof articles === 'undefined') {
      return
    }
    const addons: TeaserBoxAddonContent[] = []
    if (rawAddons?.length) {
      try {
        const snippetsRelated: Related<RelatedType.Snippet>[] = []
        rawAddons.map(a => {
          if (a.snippet) {
            snippetsRelated.push(a.snippet)
          }
        })

        const snippets = snippetsRelated.map(a => this.relatedService.resolveSync(a))
        snippets.map((s, i) => {
          if (!s || Array.isArray(s)) return null
          if (!s.modules?.length) {
            return null
          }
          // Ensure we get a single module, not an array
          const module = Array.isArray(s.modules[0]) ? s.modules[0][0] : s.modules[0]
          addons.push(
            {
              position: rawAddons[i].position,
              module: module as AnyModule // only one module is allowed
            }
          )
        })
      } catch (e) {
        if (this.debug) console.warn(e)
        console.info(e)
      }
    }
    const items = this.createMergedItems(articles, addons)
    this.tabs[index] = {
      ...this.tabs[index],
      items
    }

    if (!isRelated(readMoreLink)) {
      return
    }

    this.resolveReadMoreLink(readMoreLink, index)
  }

  private getArticlesCount (index: number): number {
    let articlesCount = getArticlesCountPerTab(this.moduleVersion, this.teaserBoxConfig)
    if (isTeaserBoxWithoutTabs(this.moduleVersion)) {
      articlesCount = getArticlesCountForTab(this.moduleVersion, index)
    }
    // console.log(`articlesCount ${index}`, articlesCount)
    return articlesCount
  }
}
